import SimpleMarkdown, {type SingleASTNode} from "@khanacademy/simple-markdown";
import {
    orderedListRule,
    paragraphRule,
    textRule,
    unorderedListRule,
} from "@khan/component-markdown/rules";
import type {MessageAudio} from "@khan/guide-types";

type UnTypedASTNode = Record<string, any>;

const parse = SimpleMarkdown.parserFor({
    orderedList: orderedListRule,
    unorderedList: unorderedListRule,
    paragraph: paragraphRule,

    // We include the text rule as the default rule as a passthrough. If the
    // text doesn't match any of the other rules, it will be treated as a
    // regular text node.
    text: textRule,
});

/**
 * Returns the source text with the specified chunk highlighted using markdown
 * syntax.
 *
 * Highlights a specific part of the source text given the associated audio
 * chunks and the index of the audio chunk to emphasize. This is used for
 * highlighting the text that is currently being spoken by the text-to-speech
 * feature.
 */
export const highlightSourceText = (
    sourceText: string,
    audioChunks: MessageAudio,
    chunkIndex: number,
): string => {
    // In practice, this should never happen, but in the event that the chunk
    // index we're trying to highlight is greater than the number of chunks,
    // we will not highlight anything.
    if (chunkIndex >= audioChunks.length) {
        return sourceText;
    }

    // Handle scenarios where the audioChunks provided belong to a response
    // group (Khanmigo responds with consecutive messages). In this scenario
    // the sourceText is a subset of the transcript. We determine whether the
    // chunk we are highlighting is part of the sourceText.
    let lastChunkFound = 0;
    let processedLength = 0;
    for (let i = 0; i < chunkIndex; i++) {
        if (sourceText.includes(audioChunks[i].transcript)) {
            if (i !== lastChunkFound + 1) {
                processedLength = audioChunks[i].transcript.length;
            } else {
                processedLength += audioChunks[i].transcript.length;
            }
            lastChunkFound = i;
        }
    }
    const processedSource = sourceText.slice(0, processedLength);

    // Don't want the leading space to be included in the highlight. In
    // markdown, the start delimiter for highlighting (==) should not be
    // followed by whitespace.
    const textToHighlight = audioChunks[chunkIndex].transcript.trim();
    if (!textToHighlight) {
        return sourceText;
    }

    const highlightedText = highlightText(textToHighlight);
    return (
        processedSource +
        // Replace the first instance of the text with itself wrapped in == to
        // indicate that it should be highlighted when rendered with markdown.
        sourceText
            .slice(processedLength)
            .replace(textToHighlight, highlightedText)
    );
};

/**
 * Returns a string formatted with markdown syntax to highlight the specified
 * text.
 *
 * It ignores leading/trailing whitespaces and handles special scenarios such
 * as lists where we only want to highlight the list items excluding the list
 * bullet.
 */
export const highlightText = (text: string): string => {
    // We also want to escape any equal signs in the text to highlight so that
    // they don't get interpreted as markdown syntax.
    // prettier-ignore
    // eslint-disable-next-line no-useless-escape -- porting over from old code
    const escapedTextToHighlight = text.replace(/=/g, "\=");

    // If the transcript contains a list, we only highlight the list items in
    // that list. Wrapping markdown (in this case highlights) around the entire
    // list item will break the HTML hierarchy of that list .
    const nodes = parse(escapedTextToHighlight, {
        // SimpleMarkdown appends \n\n to the end of the source text, which we
        // don't want. This disables that behavior.
        disableAutoBlockNewlines: true,
    });
    const [highlighted, source] = highlightNodes(nodes, escapedTextToHighlight);
    return escapedTextToHighlight.replace(source, highlighted);
};

/**
 * Given an AST node and a string to highlight, it returns a tuple with
 * the highlighted content and the source text that was processed.
 */
export const highlightNode = (
    node: SingleASTNode | UnTypedASTNode,
    textToHighlight: string,
): [highlighted: string, source: string] => {
    switch (node.type) {
        case "text": {
            const {content} = node;
            const nodeContentToHighlight = node.content.trim();
            // If there is only whitespace, then we do not need to highlight.
            if (!nodeContentToHighlight) {
                return ["", ""];
            }
            return [
                content.replace(
                    nodeContentToHighlight,
                    `==${nodeContentToHighlight}==`,
                ),
                content,
            ];
        }
        case "paragraph": {
            const {content} = node;
            return highlightNodes(content, textToHighlight);
        }
        case "orderedList":
        case "unorderedList": {
            const {items: listItems} = node;

            let result = "";
            let unprocessedText = textToHighlight;
            for (const listItem of listItems) {
                const [highlighted, source] = highlightNodes(
                    listItem,
                    unprocessedText,
                );
                result += highlighted;
                unprocessedText = unprocessedText.slice(source.length);
            }

            return [
                result,
                textToHighlight.substring(
                    0,
                    textToHighlight.length - unprocessedText.length,
                ),
            ];
        }
        default:
            return ["", ""];
    }
};

/**
 * Given a list of AST nodes and a string to highlight, it returns a tuple with
 * the highlighted content and the source text that was processed.
 */
export const highlightNodes = (
    nodes: SingleASTNode[],
    textToHighlight: string,
): [highlight: string, source: string] => {
    let result = "";
    let processedText = "";
    let unprocessedText = textToHighlight;

    // Combine all the text nodes that appear in succession together so that we
    // can highlight them as one rather than individually.
    nodes = _combineTextNodes(nodes);
    for (const node of nodes) {
        const [highlighted, content] = highlightNode(node, unprocessedText);
        const source = unprocessedText.substring(
            0,
            unprocessedText.indexOf(content) + content.length,
        );

        result += source.replace(content, highlighted);
        unprocessedText = unprocessedText.slice(source.length);
        processedText += source;
    }

    return [result, processedText];
};

/**
 * Iterates through the list of nodes and combines all the text nodes that
 * appear in succession. This is useful for highlighting the text together
 * rather than individually.
 */
const _combineTextNodes = (nodes: SingleASTNode[]): SingleASTNode[] => {
    const result: SingleASTNode[] = [];
    let bufferedText = "";

    const appendBufferedText = () => {
        if (bufferedText === "") {
            return;
        }
        result.push({
            content: bufferedText,
            type: "text",
        });
        bufferedText = "";
    };

    for (const node of nodes) {
        if (node.type !== "text") {
            appendBufferedText();
            result.push(node);
            continue;
        }
        bufferedText += node.content;
    }

    appendBufferedText();
    return result;
};
